"use client";

// src/lib/i18n/date/use-date-formatter.ts
import { useLocale } from "next-intl";
import { useMemo } from "react";
import type { Locale } from "@/lib/i18n";
import { formatDate } from "./formatters";
import type { PresetName } from "./presets";
import { formatRelative } from "./relative";
import type { DateInput } from "./to-date";

/** Client-side locale-aware date formatter, bound to the active next-intl locale. */
export function useDateFormatter() {
  const locale = useLocale() as Locale;
  return useMemo(() => {
    const make = (preset: PresetName) => (input: DateInput) =>
      formatDate(input, { locale, preset });
    return {
      format: (input: DateInput, preset: PresetName) =>
        formatDate(input, { locale, preset }),
      relative: (input: DateInput) => formatRelative(input, locale),
      numeric: make("numeric"),
      long: make("long"),
      monthDay: make("monthDay"),
      weekdayLong: make("weekdayLong"),
      dateTime: make("dateTime"),
      dateTimeMarker: make("dateTimeMarker"),
      longDateTime: make("longDateTime"),
    };
  }, [locale]);
}
